import type { SupabaseClient } from "@supabase/supabase-js";

// Audit event taxonomy read model — public.audit_event_taxonomy (Task 62c, 3dee1aa). Advisory
// classification for audit rows: category (scan / privacy / platform_security / …) and
// sensitivity (standard / elevated / restricted). Authenticated users may read ACTIVE rows only;
// mutations are service-owned. Near-static reference data: fetch once per surface mount.
// Unknown event types simply have no entry — the UI claims nothing rather than guessing.

export interface AuditTaxonomyEntry {
  eventType: string;
  category: string;
  sensitivity: "standard" | "elevated" | "restricted";
}

const SENSITIVITIES = new Set(["standard", "elevated", "restricted"]);

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function mapAuditTaxonomyRow(raw: Record<string, unknown>): AuditTaxonomyEntry | null {
  const eventType = asString(raw.event_type);
  const category = asString(raw.category);
  const sensitivity = asString(raw.sensitivity);
  if (eventType === null || category === null || sensitivity === null || !SENSITIVITIES.has(sensitivity)) {
    return null;
  }
  return { eventType, category, sensitivity: sensitivity as AuditTaxonomyEntry["sensitivity"] };
}

/** Active taxonomy rows keyed by event type. Errors resolve to an EMPTY map — the audit feed
 *  degrades to its unclassified rendering instead of failing the whole surface. */
export async function fetchAuditTaxonomy(client: SupabaseClient): Promise<Map<string, AuditTaxonomyEntry>> {
  const { data, error } = await client
    .from("audit_event_taxonomy")
    .select("event_type, category, sensitivity")
    .eq("active", true);
  if (error) {
    return new Map();
  }
  const rows: unknown[] = Array.isArray(data) ? data : [];
  const entries = rows
    .filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null)
    .map(mapAuditTaxonomyRow)
    .filter((entry): entry is AuditTaxonomyEntry => entry !== null);
  return new Map(entries.map((entry) => [entry.eventType, entry]));
}
